import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Switch,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Divider,
  Chip,
  Badge
} from "@mui/material";

// Icons
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MenuBookIcon from "@mui/icons-material/MenuBook"; // Learn icon
import RefreshIcon from "@mui/icons-material/Refresh"; // Review icon
import SearchIcon from "@mui/icons-material/Search";
import ViewModuleIcon from "@mui/icons-material/ViewModule"; // Card view icon
import ViewListIcon from "@mui/icons-material/ViewList"; // List view icon
import ClearIcon from "@mui/icons-material/Clear"; // Clear search icon
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"; // Info icon
import StarIcon from "@mui/icons-material/Star"; // Favorite icon
import StarOutlineIcon from "@mui/icons-material/StarOutline"; // Unfavorite icon
import MoreVertIcon from "@mui/icons-material/MoreVert"; // More options icon

import { jwtDecode } from "jwt-decode";
import api from "../../api";

const DeckList = ({ token }) => {
  // Core state
  const [decks, setDecks] = useState({ publicDecks: [], userDecks: [] });
  const [publicExpanded, setPublicExpanded] = useState(true);
  const [userExpanded, setUserExpanded] = useState(true);
  const [hoveredDeck, setHoveredDeck] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [viewMode, setViewMode] = useState("card"); // 'card' or 'list'
  const [searchQuery, setSearchQuery] = useState("");
  const [favoriteDecks, setFavoriteDecks] = useState([]);

  // Modal state
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [newDeckTitle, setNewDeckTitle] = useState("");
  const [newDeckDescription, setNewDeckDescription] = useState("");
  const [newDeckType, setNewDeckType] = useState("regular");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");

  const navigate = useNavigate();
  let userId;

  try {
    const decoded = jwtDecode(token);
    userId = decoded?.user?._id;
  } catch (e) {
    console.error("Error decoding token:", e);
  }

  // Initial data loading
  useEffect(() => {
    setLoading(true);
    api
      .get("/deck-list")
      .then((response) => {
        setDecks(response.data);
        setError(null);
      })
      .catch((error) => {
        console.error("Error fetching deck list:", error);
        setError("Failed to load decks. Please try again later.");
      })
      .finally(() => {
        setLoading(false);
      });

    // Load favorite decks from localStorage
    const storedFavorites = localStorage.getItem('favoriteDecks');
    if (storedFavorites) {
      setFavoriteDecks(JSON.parse(storedFavorites));
    }
  }, [token]);

  // Filtered decks based on search query
  const filteredDecks = useMemo(() => {
    const filterDecks = (deckList) => {
      if (!searchQuery.trim()) return deckList;

      return deckList.filter(deck =>
        deck.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        deck.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    };

    return {
      publicDecks: filterDecks(decks.publicDecks),
      userDecks: filterDecks(decks.userDecks)
    };
  }, [decks, searchQuery]);

  // Navigation handlers
  const handleDeckClick = (deckId) => navigate(`/decks/${deckId}`);

  const handleLearnClick = (e, deckId, deckType) => {
    e.stopPropagation(); // Prevent triggering the card click
    navigate(`${deckType === "multi-step" ? "/multi-step-" : "/"}learn/${deckId}`);
  };

  const handleReviewClick = (e, deckId) => {
    e.stopPropagation(); // Prevent triggering the card click
    navigate(`/review/${deckId}`);
  };

  // Modal handlers
  const openDeleteDeckModal = (e, deck) => {
    e.stopPropagation(); // Prevent triggering the card click
    setDeckToDelete(deck);
    setDeleteConfirmation("");
    setOpenDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeckToDelete(null);
    setDeleteConfirmation("");
  };

  const confirmDeleteDeck = async () => {
    if (!deckToDelete) return;
    setIsSubmitting(true);

    try {
      await api.delete(`/decks/${deckToDelete._id}`);
      setDecks((prev) => ({
        ...prev,
        userDecks: prev.userDecks.filter((d) => d._id !== deckToDelete._id),
      }));

      // Also remove from favorites if present
      if (favoriteDecks.includes(deckToDelete._id)) {
        const newFavorites = favoriteDecks.filter(id => id !== deckToDelete._id);
        setFavoriteDecks(newFavorites);
        localStorage.setItem('favoriteDecks', JSON.stringify(newFavorites));
      }
    } catch (error) {
      console.error("Error deleting deck:", error);
      setError("Failed to delete deck. Please try again.");
    } finally {
      setIsSubmitting(false);
      closeDeleteModal();
    }
  };

  const openCreateDeckModal = () => {
    setNewDeckTitle("");
    setNewDeckDescription("");
    setNewDeckType("regular");
    setOpenCreateModal(true);
  };

  const closeCreateModal = () => {
    setOpenCreateModal(false);
    setNewDeckTitle("");
    setNewDeckDescription("");
    setNewDeckType("regular");
  };

  const handleCreateDeck = async () => {
    if (!newDeckTitle.trim()) return;

    setIsSubmitting(true);
    try {
      const newDeckData = {
        title: newDeckTitle,
        description: newDeckDescription,
        isPublic: false,
        createdBy: userId,
        deckType: newDeckType
      };

      const response = await api.post("/decks/create", newDeckData);
      const createdDeck = response.data.deck;

      // Add empty counts for UI consistency
      createdDeck.pendingToLearnCount = 0;
      createdDeck.pendingReviewCount = 0;

      setDecks((prev) => ({
        ...prev,
        userDecks: [...prev.userDecks, createdDeck],
      }));

      // Navigate to the new deck to add cards immediately
      navigate(`/decks/${createdDeck._id}`);
    } catch (error) {
      console.error("Error creating deck:", error);
      setError("Failed to create deck. Please try again.");
    } finally {
      setIsSubmitting(false);
      closeCreateModal();
    }
  };

  // Function to handle navigation to the appropriate add card page based on deck type
  const handleAddCardClick = (e, deck) => {
    e.stopPropagation(); // Prevent triggering the card click
    if (deck.deckType === "multi-step") {
      navigate(`/decks/${deck._id}/add-multi-step-card`);
    } else {
      navigate(`/decks/${deck._id}/add-card`);
    }
  };

  // Handle favorites
  const toggleFavorite = (e, deckId) => {
    e.stopPropagation(); // Prevent triggering the card click

    let newFavorites;
    if (favoriteDecks.includes(deckId)) {
      newFavorites = favoriteDecks.filter(id => id !== deckId);
    } else {
      newFavorites = [...favoriteDecks, deckId];
    }

    setFavoriteDecks(newFavorites);
    localStorage.setItem('favoriteDecks', JSON.stringify(newFavorites));
  };

  // Clear search query
  const handleClearSearch = () => setSearchQuery("");

  // Enhanced color palette
  const colors = {
    learn: {
      main: "#1565C0",
      light: "#2196F3",
      dark: "#0D47A1",
      gradient: "linear-gradient(to right,rgb(142, 186, 251),rgb(61, 151, 255),rgb(5, 89, 158))"
    },
    review: {
      main: "#1565C0",
      light: "#2196F3",
      dark: "#0D47A1",
      gradient: "linear-gradient(to right,rgb(142, 186, 251),rgb(61, 151, 255),rgb(5, 89, 158))"
    },
    delete: {
      main: "#C62828",
      light: "#EF5350",
      dark: "#B71C1C",
      gradient: "linear-gradient(to right, #B71C1C, #C62828, #EF5350)"
    },
    neutral: {
      background: "#F5F5F5",
      text: "#333333",
      border: "#E0E0E0"
    },
    favorite: {
      active: "#FFD700", // Gold color for favorite
      inactive: "#9E9E9E" // Gray for unfavorited
    }
  };

  const typography = {
    fontFamily: "'Inter', 'Roboto', sans-serif",
    fontSize: {
      small: "0.875rem",
      medium: "1rem",
      large: "1.25rem"
    }
  };

  const shadows = {
    light: "0 4px 6px rgba(0,0,0,0.1)",
    medium: "0 6px 12px rgba(0,0,0,0.15)",
    strong: "0 10px 20px rgba(0,0,0,0.19)"
  };

  const cardStyles = {
    boxShadow: 3,
    borderRadius: 3,
    transition: "0.3s",
    "&:hover": {
      boxShadow: 6,
      transform: "translateY(-5px)"
    }
  };

  const buttonStyles = {
    learn: {
      backgroundColor: colors.learn.main,
      color: "white",
      "&:hover": {
        backgroundColor: colors.learn.dark
      }
    },
    review: {
      backgroundColor: colors.review.main,
      color: "white",
      "&:hover": {
        backgroundColor: colors.review.dark
      }
    }
  };

  const countBadgeStyles = {
    ml: 1,
    px: 1,
    borderRadius: 2,
    bgcolor: "white",
    color: "black",
    fontWeight: "bold"
  };

  // Render helpers
  const renderCardView = (deckList, isUserDeck = false) => (
    <Grid container spacing={3}>
      {deckList.map((deck) => {
        const isFavorite = favoriteDecks.includes(deck._id);

        return (
          <Grid item xs={12} sm={6} md={4} key={deck._id}>
            <Card
              sx={{
                ...cardStyles,
                ...(isFavorite && { border: `2px solid ${colors.favorite.active}` })
              }}
              onClick={() => handleDeckClick(deck._id)}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: colors.learn.dark
                    }}
                  >
                    {deck.title}
                    {deck.deckType === "multi-step" && (
                      <Tooltip title="Multi-step deck">
                        <Chip
                          size="small"
                          label="Multi-step"
                          sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                        />
                      </Tooltip>
                    )}
                  </Typography>

                  <Box>
                    <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
                      <IconButton
                        size="small"
                        onClick={(e) => toggleFavorite(e, deck._id)}
                        sx={{ color: isFavorite ? colors.favorite.active : colors.favorite.inactive }}
                      >
                        {isFavorite ? <StarIcon /> : <StarOutlineIcon />}
                      </IconButton>
                    </Tooltip>

                    {isUserDeck && (
                      <Tooltip title="Delete deck">
                        <IconButton
                          size="small"
                          sx={{
                            color: colors.delete.main,
                            "&:hover": { color: colors.delete.dark }
                          }}
                          onClick={(e) => openDeleteDeckModal(e, deck)}
                          onMouseEnter={() => setHoveredDeck(deck._id)}
                          onMouseLeave={() => setHoveredDeck(null)}
                        >
                          {hoveredDeck === deck._id ? (
                            <RestoreFromTrashIcon />
                          ) : (
                            <DeleteOutlineIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: "gray",
                    mb: 2,
                    height: '2.5em',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {deck.description || "No description provided"}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Box sx={{ display: "flex", gap: 1, flexGrow: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      sx={buttonStyles.learn}
                      startIcon={<MenuBookIcon />}
                      onClick={(e) => handleLearnClick(e, deck._id, deck.deckType)}
                    >
                      Learn
                      <Box component="span" sx={countBadgeStyles}>
                        {deck.pendingToLearnCount ?? 0}
                      </Box>
                    </Button>

                    <Button
                      variant="contained"
                      size="small"
                      sx={buttonStyles.review}
                      startIcon={<RefreshIcon />}
                      onClick={(e) => handleReviewClick(e, deck._id)}
                    >
                      Review
                      <Box component="span" sx={countBadgeStyles}>
                        {deck.pendingReviewCount ?? 0}
                      </Box>
                    </Button>
                  </Box>

                  {isUserDeck && deck.createdBy === userId && (
                    <Tooltip title="Add new card">
                      <Button
                        variant="contained"
                        size="small"
                        sx={{
                          minWidth: "auto",
                          px: 1.5,
                          backgroundColor: colors.learn.light,
                          color: 'white',
                          "&:hover": {
                            backgroundColor: colors.learn.main
                          },
                          fontWeight: 'bold'
                        }}
                        onClick={(e) => handleAddCardClick(e, deck)}
                      >
                        <AddIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        );
      })}

      {/* Create Deck Card (only show in user decks section) */}
      {isUserDeck && (
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              border: `2px dashed ${colors.review.main}`,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
              cursor: "pointer",
              transition: "0.3s",
              "&:hover": {
                backgroundColor: colors.review.light + "33", // 20% opacity
                transform: "scale(1.05)"
              }
            }}
            onClick={openCreateDeckModal}
          >
            <Box sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  backgroundColor: colors.review.main,
                  color: "white",
                  borderRadius: "50%",
                  width: 60,
                  height: 60,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "auto",
                  boxShadow: 3
                }}
              >
                <AddIcon fontSize="large" />
              </Box>
              <Typography variant="h6" sx={{ mt: 2, color: colors.review.dark }}>
                Create New Deck
              </Typography>
            </Box>
          </Card>
        </Grid>
      )}
    </Grid>
  );

  const renderListView = (deckList, isUserDeck = false) => (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <List>
        {deckList.map((deck, index) => {
          const isFavorite = favoriteDecks.includes(deck._id);

          return (
            <React.Fragment key={deck._id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                button
                onClick={() => handleDeckClick(deck._id)}
                sx={{
                  transition: '0.2s',
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  ...(isFavorite && { borderLeft: `4px solid ${colors.favorite.active}` })
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {isFavorite && (
                        <StarIcon
                          fontSize="small"
                          sx={{ color: colors.favorite.active, mr: 1 }}
                        />
                      )}
                      <Typography variant="subtitle1" fontWeight="bold">
                        {deck.title}
                      </Typography>
                      {deck.deckType === "multi-step" && (
                        <Chip
                          size="small"
                          label="Multi-step"
                          sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {deck.description || "No description provided"}
                    </Typography>
                  }
                />

                <ListItemSecondaryAction>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Learn cards">
                      <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        startIcon={<MenuBookIcon />}
                        onClick={(e) => handleLearnClick(e, deck._id, deck.deckType)}
                        sx={{ mr: 1 }}
                      >
                        <Badge badgeContent={deck.pendingToLearnCount ?? 0} color="primary">
                          Learn
                        </Badge>
                      </Button>
                    </Tooltip>

                    <Tooltip title="Review cards">
                      <Button
                        variant="outlined"
                        size="small"
                        color="primary"
                        startIcon={<RefreshIcon />}
                        onClick={(e) => handleReviewClick(e, deck._id)}
                      >
                        <Badge badgeContent={deck.pendingReviewCount ?? 0} color="primary">
                          Review
                        </Badge>
                      </Button>
                    </Tooltip>

                    {isUserDeck && (
                      <>
                        <Tooltip title="Add card">
                          <IconButton
                            edge="end"
                            size="small"
                            color="primary"
                            onClick={(e) => handleAddCardClick(e, deck)}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"}>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => toggleFavorite(e, deck._id)}
                            sx={{ color: isFavorite ? colors.favorite.active : colors.favorite.inactive }}
                          >
                            {isFavorite ? <StarIcon /> : <StarOutlineIcon />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete deck">
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={(e) => openDeleteDeckModal(e, deck)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </ListItemSecondaryAction>
              </ListItem>
            </React.Fragment>
          );
        })}

        {isUserDeck && (
          <>
            <Divider />
            <ListItem
              button
              onClick={openCreateDeckModal}
              sx={{
                color: colors.review.main,
                transition: '0.2s',
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <AddIcon sx={{ mr: 1 }} />
              <ListItemText primary="Create New Deck" />
            </ListItem>
          </>
        )}
      </List>
    </Paper>
  );

  const noDecksMessage = (isUserDeck) => (
    <Paper
      elevation={2}
      sx={{
        p: 4,
        textAlign: 'center',
        borderRadius: 2,
        backgroundColor: '#f9f9f9'
      }}
    >
      <Typography variant="h6" color="text.secondary" gutterBottom>
        {isUserDeck
          ? "You don't have any decks yet"
          : "No public decks available"}
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        {isUserDeck
          ? "Create your first flashcard deck to start learning"
          : "Check back later for public decks or create your own"}
      </Typography>

      {isUserDeck && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreateDeckModal}
          sx={{ mt: 2 }}
        >
          Create Your First Deck
        </Button>
      )}
    </Paper>
  );

  return (
    <Container sx={{ maxWidth: "90%", mt: 4, mb: 8 }}>
      {/* Header with search and view toggle */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Your Flashcard Decks
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Create, study and review your flashcards. Track your progress and master any subject with spaced repetition.
        </Typography>

        {/* Help Text */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mt: 4,
            mb: 2,
            backgroundColor: '#F5F9FF',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            borderRadius: 2
          }}
        >
          <InfoOutlinedIcon color="primary" />
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
              Quick Tips:
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Learn</strong> new cards to memorize them for the first time
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Review</strong> cards based on your spaced repetition schedule
            </Typography>
            <Typography variant="body2" paragraph>
              • <strong>Star</strong> your most important decks to mark them as favorites
            </Typography>
            <Typography variant="body2">
              • Switch between <strong>card view</strong> and <strong>list view</strong> using the toggle
            </Typography>
          </Box>
        </Paper>

        <Box sx={{
          mt: 3,
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}>
          {/* Search bar */}
          <TextField
            placeholder="Search decks..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flexGrow: 1,
              maxWidth: { xs: '100%', sm: '50%' },
              backgroundColor: 'white'
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          {/* View toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={viewMode === 'list'}
                  onChange={() => setViewMode(viewMode === 'card' ? 'list' : 'card')}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {viewMode === 'card' ? <ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} /> : <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />}
                  <Typography variant="body2">
                    {viewMode === 'card' ? 'Card View' : 'List View'}
                  </Typography>
                </Box>
              }
            />

            <Tooltip title="Create new deck">
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openCreateDeckModal}
              >
                New Deck
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Error message */}
      {error && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            bgcolor: '#FFEBEE',
            color: '#C62828',
            borderRadius: 2
          }}
        >
          <Typography>{error}</Typography>
        </Paper>
      )}

      {/* Loading state */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Public Decks Section */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: colors.review.light
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "white" }}>
              Public Decks
              {searchQuery && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  size="small"
                  onDelete={handleClearSearch}
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            <IconButton onClick={() => setPublicExpanded(!publicExpanded)}>
              {publicExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={publicExpanded}>
            {filteredDecks.publicDecks.length > 0 ? (
              viewMode === 'card'
                ? renderCardView(filteredDecks.publicDecks)
                : renderListView(filteredDecks.publicDecks)
            ) : (
              searchQuery
                ? <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>No public decks match your search</Typography>
                : noDecksMessage(false)
            )}
          </Collapse>

          {/* User Decks Section */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              mt: 4,
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: colors.learn.light
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold", color: "white" }}>
              Your Decks
              {searchQuery && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  size="small"
                  onDelete={handleClearSearch}
                  sx={{ ml: 2 }}
                />
              )}
            </Typography>
            <IconButton onClick={() => setUserExpanded(!userExpanded)}>
              {userExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>

          <Collapse in={userExpanded}>
            {filteredDecks.userDecks.length > 0 ? (
              viewMode === 'card'
                ? renderCardView(filteredDecks.userDecks, true)
                : renderListView(filteredDecks.userDecks, true)
            ) : (
              searchQuery
                ? <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>No decks match your search</Typography>
                : noDecksMessage(true)
            )}
          </Collapse>

          {/* Favorite Decks Section (only show if there are favorites) */}
          {favoriteDecks.length > 0 && !searchQuery && (
            <>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  mt: 4,
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: colors.favorite.active,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold", color: "black" }}>
                  Favorite Decks
                  <StarIcon sx={{ ml: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} />
                </Typography>
              </Box>

              {viewMode === 'card'
                ? renderCardView([...decks.publicDecks, ...decks.userDecks].filter(deck => favoriteDecks.includes(deck._id)))
                : renderListView([...decks.publicDecks, ...decks.userDecks].filter(deck => favoriteDecks.includes(deck._id)))
              }
            </>
          )}

          {/* ----------------------------- CREATE DECK MODAL ----------------------------- */}
          <Dialog
            open={openCreateModal}
            onClose={closeCreateModal}
            fullWidth
            maxWidth="sm"
            TransitionProps={{
              onEnter: () => {
                // Use a less expensive initial render while transitioning
                document.body.style.cursor = 'default';
              }
            }}
          >
            <DialogTitle sx={{ pb: 1 }}>Create a New Deck</DialogTitle>
            <DialogContent>
              <Box mt={2} mb={2}>
                <TextField
                  label="Deck Title"
                  variant="outlined"
                  fullWidth
                  value={newDeckTitle}
                  onChange={(e) => setNewDeckTitle(e.target.value)}
                  sx={{ mb: 2 }}
                  // Using autoComplete off and a random key to help with lag
                  autoComplete="off"
                  key={`title-${Math.random()}`}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextField
                  label="Description"
                  variant="outlined"
                  fullWidth
                  value={newDeckDescription}
                  onChange={(e) => setNewDeckDescription(e.target.value)}
                  multiline
                  rows={3}
                  sx={{ mb: 2 }}
                  // Using autoComplete off and a random key to help with lag
                  autoComplete="off"
                  key={`desc-${Math.random()}`}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <FormControl fullWidth>
                  <InputLabel id="deck-type-label">Deck Type</InputLabel>
                  <Select
                    labelId="deck-type-label"
                    id="deck-type-select"
                    value={newDeckType}
                    label="Deck Type"
                    onChange={(e) => setNewDeckType(e.target.value)}
                  >
                    <MenuItem value="regular">Simple Flashcards</MenuItem>
                    <MenuItem value="multi-step">Multi-Step Learning Path</MenuItem>
                  </Select>
                  <Typography variant="caption" sx={{ mt: 1, color: 'text.secondary' }}>
                    {newDeckType === 'regular'
                      ? 'Simple flashcards have a question and answer format.'
                      : 'Multi-step cards break down complex topics into sequential learning steps.'}
                  </Typography>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeCreateModal}>Cancel</Button>
              <Button
                onClick={handleCreateDeck}
                variant="contained"
                disabled={!newDeckTitle.trim() || isSubmitting}
                startIcon={isSubmitting && <CircularProgress size={18} />}
              >
                {isSubmitting ? 'Creating...' : 'Create Deck'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* ----------------------------- DELETE DECK MODAL ----------------------------- */}
          <Dialog open={openDeleteModal} onClose={closeDeleteModal} fullWidth maxWidth="sm">
            <DialogTitle sx={{ color: colors.delete.main }}>Delete Deck</DialogTitle>
            <DialogContent>
              {deckToDelete && (
                <>
                  <Typography paragraph>
                    Are you sure you want to delete <strong>{deckToDelete.title}</strong>?
                    This action cannot be undone and all cards in this deck will be permanently removed.
                  </Typography>
                  <Typography>
                    To confirm, type <strong>{deckToDelete.title}</strong> below:
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    sx={{ mt: 2 }}
                    error={deleteConfirmation !== "" && deleteConfirmation !== deckToDelete.title}
                    autoComplete="off"
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDeleteModal}>Cancel</Button>
              <Button
                onClick={confirmDeleteDeck}
                variant="contained"
                color="error"
                disabled={
                  !deckToDelete ||
                  deleteConfirmation !== (deckToDelete?.title || "") ||
                  isSubmitting
                }
                startIcon={isSubmitting && <CircularProgress size={18} color="inherit" />}
              >
                {isSubmitting ? 'Deleting...' : 'Delete Permanently'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}


    </Container>
  );
};

export default DeckList;